import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";

/**
 * Blocks the entire app for authenticated users who haven't provided a phone
 * number yet. This runs the moment auth resolves — nothing on the page is
 * interactive until they save a WhatsApp number or sign out.
 *
 * Emergent Google OAuth does not return a phone, so we collect it post-login.
 */
export default function PhoneRequiredGate() {
  const { user, loading, refresh, logout } = useAuth();
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  // Don't render while auth is still resolving, if not signed in, or if the
  // user already has a phone on file (including seeded admins).
  if (loading || !user || user.whatsapp) return null;

  const save = async () => {
    const trimmed = phone.trim();
    if (!trimmed) { toast.error("Enter your WhatsApp number"); return; }
    setSaving(true);
    try {
      await api.post("/auth/complete-profile", { whatsapp: trimmed });
      await refresh();
      toast.success("Thanks — your WhatsApp is saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that number");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="phone-required-gate"
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      // Non-dismissable — no onClick outside handler.
    >
      <div className="bg-white border-2 border-black w-full max-w-md p-6 sm:p-8 shadow-[8px_8px_0_0_#000]">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#25D366] text-white grid place-items-center">
            <MessageCircle className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold">One quick step</div>
            <h2 className="font-display font-black text-2xl leading-tight">Add your WhatsApp</h2>
          </div>
        </div>

        <p className="text-sm text-neutral-700 mb-5">
          We use your WhatsApp number to send order updates and let sellers reach you
          about your purchases. We won't share it with anyone else.
        </p>

        <label className="text-[11px] uppercase tracking-widest font-bold text-neutral-700">
          WhatsApp number
        </label>
        <input
          data-testid="phone-gate-input"
          autoFocus
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && save()}
          placeholder="+592 600 1234 or 6001234"
          inputMode="tel"
          className="w-full mt-1 h-12 px-3 border-2 border-black rounded-none text-base focus:outline-none focus:ring-2 focus:ring-[#0033FF]"
        />
        <p className="text-[11px] text-neutral-500 mt-1">
          Guyana numbers (+592) are auto-prefixed if you type just the local part.
        </p>

        <button
          data-testid="phone-gate-submit"
          onClick={save}
          disabled={saving || !phone.trim()}
          className="mt-6 w-full h-12 bg-black text-white font-bold uppercase tracking-widest text-sm hover:bg-neutral-900 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save & continue"}
        </button>

        <button
          data-testid="phone-gate-signout"
          onClick={logout}
          className="mt-3 w-full h-10 border border-neutral-300 text-neutral-600 text-xs hover:border-black hover:text-black"
        >
          Or sign out
        </button>
      </div>
    </div>
  );
}
